import { Box, Dialog, DialogContent, TextField, Typography } from "@mui/material"
import { DesktopDatePicker } from "@mui/x-date-pickers-pro"
import { BoxFC, BoxFR } from "components/BoxCustom"
import DataGridCellExpand from "components/DataGridCellExpand/DataGridCellExpand"
import PaperComponent from "components/PaperComponent"
import { DialogFooter, DialogHeader } from "components/dialog/DialogHeader"
import { alertError, alertSuccess } from "components/Alert"
import { GlobalStateContext } from "contexts/GlobalStateContext"
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import dayjs from "dayjs"
import { numberFormat } from "utils"

/**
 * File this driver's ค้างจ่ายไม่ระบุงวด — wage lines entered where the work is, as their own
 * pc_transaction in PCJnlId 100, that were never given a งวด — into this payroll's month. Filing
 * only stamps the งวด on the line's own document (salary_pc_transaction); that stamp is what puts
 * it in the payroll, so nothing is copied and no amount is duplicated.
 */
const ClaimAccrualDialog = ({ dialogOpen, setDialogOpen, drvId, drvNm, salDte, onFinish }) => {
  const { ax } = useContext(GlobalStateContext)
  const [dataTable, setDataTable] = useState([])
  const [selectionModel, setSelectionModel] = useState([])
  const [accrTmEn, setAccrTmEn] = useState(null)

  const getData = useCallback(() => {
    ax.post("/branch/driver-payroll-v3/getUnfiledAccruals", {
      DrvId: drvId,
      AccrTmSt: accrTmEn ? "2000-01-01" : null,
      AccrTmEn: accrTmEn ? dayjs(accrTmEn).format("YYYY-MM-DD") : null,
    }).then(value => {
      if (value.data) { setDataTable(value.data); setSelectionModel([]) }
    })
  }, [ax, drvId, accrTmEn])

  useEffect(() => {
    if (dialogOpen) setAccrTmEn(dayjs(salDte).endOf("month"))
  }, [dialogOpen, salDte])

  useEffect(() => { if (dialogOpen) getData() }, [dialogOpen, getData])

  const selectedTotal = useMemo(() => dataTable
    .filter(r => selectionModel.includes(r.id))
    .reduce((s, r) => s + (r.InOutTyp === "I" ? -1 : 1) * +r.Amnt, 0), [dataTable, selectionModel])

  const handleClaim = useCallback(() => {
    if (selectionModel.length === 0) { alertError("กรุณาเลือกรายการที่ต้องการดึงเข้างวด"); return }
    ax.post("/branch/driver-payroll-v3/setWageItemsMonth", {
      ToSalDte: dayjs(salDte).format("YYYY-MM-DD"),
      WgItmIds: selectionModel,
    }).then(value => {
      if (!value.data) return
      const { moved, skipped, warnings } = value.data
      if (skipped > 0) alertError(`ดึงเข้างวด ${moved} รายการ ข้าม ${skipped} รายการ\n`
        + warnings.map(w => `- ${w.message}`).join("\n"))
      else alertSuccess(`ดึงเข้างวดแล้ว ${moved} รายการ`)
      setDialogOpen(false)
      if (onFinish) onFinish()
    })
  }, [ax, selectionModel, salDte, setDialogOpen, onFinish])

  const columns = useMemo(() => [
    {
      field: "StsTm", headerName: "วันที่วิ่งงาน", width: 120,
      valueFormatter: ({ value }) => value ? dayjs(value).format("DD/MM/YY HH:mm") : "-",
    },
    { field: "JobOrdId", headerName: "ใบงาน", width: 80 },
    {
      field: "ExpNm", headerName: "รายการ", flex: 1, minWidth: 160,
      renderCell: ({ row }) => (
        <Typography variant="body2">{row.ExpNm}{row.ContNo ? ` (${row.ContNo})` : ""}</Typography>
      ),
    },
    { field: "JobSNm", headerName: "งาน", width: 110 },
    {
      field: "Amnt", headerName: "จำนวน", width: 90, type: "number", align: "right", headerAlign: "right",
      renderCell: ({ row }) => (
        <Typography variant="body2" color={row.InOutTyp === "I" ? "error.main" : "text.primary"}>
          {numberFormat(row.Amnt)}
        </Typography>
      ),
    },
    {
      field: "AccrTm", headerName: "วันที่ลงค้างจ่าย", width: 130,
      valueFormatter: ({ value }) => value ? dayjs(value).format("DD/MM/YY HH:mm") : "-",
    },
  ], [])

  return (
    <Dialog open={dialogOpen} PaperComponent={PaperComponent} fullWidth maxWidth="lg">
      <DialogHeader handleDialogClose={() => setDialogOpen(false)}
        title={`ดึงรายการค้างจ่าย — ${drvNm || ""} เข้างวด ${dayjs(salDte).format("MM/YYYY")}`}
        onDeleteClick={null} />
      <DialogContent>
        <BoxFC pt={2} sx={{ gap: 1 }}>
          <BoxFR sx={{ justifyContent: "flex-end" }}>
            <Typography variant="body2" color="text.secondary">รายการค้างจ่ายที่ยังไม่ระบุงวด</Typography>
            <Box flex={1} />
            <DesktopDatePicker label="ค้างจ่ายถึงวันที่" disableMaskedInput inputFormat="DD/MM/YYYY"
              value={accrTmEn} onChange={setAccrTmEn}
              renderInput={(params) => <TextField size="small" sx={{ width: 160 }} {...params} />} />
          </BoxFR>

          <Box sx={{ height: 420 }}>
            <DataGridCellExpand
              hideFooter
              density="compact"
              checkboxSelection
              disableSelectionOnClick
              selectionModel={selectionModel}
              onSelectionModelChange={setSelectionModel}
              rows={dataTable}
              columns={columns} />
          </Box>

          <BoxFR sx={{ justifyContent: "flex-end" }}>
            <Typography variant="body2">เลือก {selectionModel.length} รายการ</Typography>
            <Typography variant="subtitle1" fontWeight="bold">{numberFormat(selectedTotal)}</Typography>
          </BoxFR>
        </BoxFC>
      </DialogContent>
      <DialogFooter okText="ดึงเข้างวด"
        handleDialogClose={() => setDialogOpen(false)}
        handleDialogOk={handleClaim} />
    </Dialog>
  )
}

export default React.memo(ClaimAccrualDialog)
